/**
 * Looking elements up by id, with the type narrowing done once.
 *
 * `getElementById` returns `HTMLElement | null`, so without this every call site either
 * repeats the same null check or asserts its way past it. Failing loudly at startup is
 * the right answer: a missing element means the markup and the script disagree, and no
 * amount of defensive coding downstream will produce a working page.
 */

export function element(id: string): HTMLElement {
  const found = document.getElementById(id);
  if (!found) throw new Error(`The page is missing the element #${id}.`);
  return found;
}

export function inputElement(id: string): HTMLInputElement {
  return narrow(id, HTMLInputElement, 'an input');
}

export function buttonElement(id: string): HTMLButtonElement {
  return narrow(id, HTMLButtonElement, 'a button');
}

export function formElement(id: string): HTMLFormElement {
  return narrow(id, HTMLFormElement, 'a form');
}

export function dialogElement(id: string): HTMLDialogElement {
  return narrow(id, HTMLDialogElement, 'a dialog');
}

function narrow<T extends HTMLElement>(
  id: string,
  type: abstract new () => T,
  description: string,
): T {
  const found = element(id);
  if (!(found instanceof type)) {
    throw new Error(`The element #${id} is not ${description}.`);
  }
  return found;
}
